import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Numbers.css";

interface NumberItem {
  id: number;
  number: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Numbers: React.FC = () => {
  const [data, setData] = useState<NumberItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/numbers`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching numbers:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: NumberItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: NumberItem) => {
    const numberString = item.number.toString();
    if (currentlyPlaying === numberString) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(numberString);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const titleMap = {
    en: "Numbers",
    de: "Zahlen",
    ta: "எண்கள்",
    si: "අංක",
    hi: "संख्याएँ",
    ar: "الأرقام",
    tr: "Sayılar",
    ru: "Числа",
    zh: "数字",
    fr: "Nombres",
    es: "Números",
  };

  const subtitleMap = {
    en: "German Numbers",
    de: "Deutsche Zahlen",
    ta: "ஜெர்மன் எண்கள்",
    si: "ජර්මන් අංක",
    hi: "जर्मन संख्याएँ",
    ar: "الأرقام الألمانية",
    tr: "Almanca Sayılar",
    ru: "Немецкие числа",
    zh: "德语数字",
    fr: "Nombres allemands",
    es: "Números alemanes",
  };

  return (
    <div className="numbers-page-container">
      <div className="numbers-page-header">
        <div>
          <h1 className="numbers-page-title">{titleMap["de"]}</h1>
          <p className="numbers-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="numbers-page-loading">Loading...</div>
      ) : (
        <div className="numbers-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.number.toString();
            return (
              <div
                key={item.id}
                className={`numbers-page-card ${
                  isPlaying ? "numbers-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="numbers-page-image-wrapper">
                  <div className="numbers-page-image-number">
                    {item.number}
                  </div>
                </div>
                <div className="numbers-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="numbers-page-translation-text">
                  {getTranslation(item)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Numbers;
